<template lang="pug">
  div.quillWrapper
    div.editor(ref="editor")
    input(ref="imageInput" type="file" accept="image/*" style="display:none;" @change="imageSelected($event)")
    input(ref="videoInput" type="file" accept="image/*" style="display:none;")
</template>

<script>
// add css class to element in quill: https://codepen.io/moghya/pen/wmKLYR

import Quill from 'quill'
import ImageResize from 'quill-image-resize-module'
import QuillImageDropAndPaste from 'quill-image-drop-and-paste'
import { base64StringToBlob } from 'blob-util'

Quill.register('modules/imageResize', ImageResize)
Quill.register('modules/imageDropAndPaste', QuillImageDropAndPaste)

// Add 'copyw' class to p tag
var Block = Quill.import('blots/block')
class CopyWritingBlock extends Block{
  static create (value) {
    let node = super.create()
    node.setAttribute('class','copyw')
    return node
  }
}
Quill.register(CopyWritingBlock, true)

// Image upload adapter
const Image = Quill.import('formats/image')
class ImageUploadPlaceholder extends Image {
  static create(value) {
    let src = value.url
    let id = value.id
    let node = super.create(src)
    if (typeof src === 'string') {
      node.setAttribute('src', this.sanitize(src))
    }
    node.setAttribute('class','copyw-image')

    if (id) {
      node.setAttribute('id', id)
    }
    return node
  }
}
ImageUploadPlaceholder.blotName = 'imageUpload'
Quill.register({
  'formats/imageUploadPlaceholder': ImageUploadPlaceholder
})

export default {
  name: 'Editor',
  props: {
    value: {
      type: String,
      default: ''
    },
    imageFileParam: {
      type: String,
      default: 'url'
    },
    imageUploadParams: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      editor: null
    }
  },
  mounted () {
    let toolbarOptions = [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'bullet' }, { 'list': 'ordered'}],
      [{ 'align': '' }, { 'align': 'center' }, { 'align': 'right' }, { 'align': 'justify' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['clean'],
      ['image', 'video', 'link']
    ]

    this.editor = new Quill(this.$refs.editor, {
      theme: 'snow',
      modules: {
        toolbar: toolbarOptions,
        // imageResize: {},
        imageDropAndPaste: {
          handler: this.imageDropped
        }
      }
    })
    this.editor.root.innerHTML = this.value
    this.editor.on('text-change', () => this.update())
    this.editor.getModule('toolbar').addHandler('image', this.imageHandler)

    // Solution for text-change not fired when after image align
    this.$refs.editor.addEventListener('click', () => {
      this.update()
    })
  },
  methods: {
    update() {
      this.$emit('input', this.editor.getText() ? this.editor.root.innerHTML : '')
    },
    imageHandler(image, callback) {
      this.$refs.imageInput.click()
    },
    imageSelected (event) {
      let file = event.target.files[0]
      this.processImageFile(file)
    },
    imageDropped (imageDataUrl, type) {
      if (!type) type = 'image/jpeg'
      let file = base64StringToBlob(imageDataUrl.replace(/^data:image\/\w+;base64,/, ''), type)
      this.processImageFile(file)
    },
    processImageFile (file) {
      if (file.size > 512000) {
        this.$showAlert('red', 'Sorry! Image exceeds size limit. Max is 512kB.')
        return
      }

      let formData = new FormData()
      formData.append('type', 'image')
      formData.append('category', 'content')
      formData.append(this.imageFileParam, file)
      Object.keys(this.imageUploadParams).forEach(k => {
        formData.append(k, this.imageUploadParams[k])
      })

      // Show base64 image first and replace with url once upload finish
      let reader = new FileReader()
      reader.onloadend = () => {
        this.editor.insertEmbed(this.editor.getSelection().index, 'imageUpload', { id: 'quill-image-placeholder', url: reader.result })
        this.$axios.post(`/medias/`, formData, { headers: { 'Content-Type': 'multipart/form-data' }}).then(resp => {
          const imageElement = document.getElementById('quill-image-placeholder')
          if (imageElement) {
            imageElement.setAttribute('src', resp.data.url)
            imageElement.removeAttribute('id')
          }
          this.$emit('onImageUpload', true)
        }).catch(e => {
          this.$emit('onImageUpload', false)
        })
      }
      reader.readAsDataURL(file)
    }
  }
}
</script>

<style lang="scss">
@import "~quill/dist/quill.core.css";
@import "~quill/dist/quill.snow.css";

.ql-editor {
  min-height: 200px;
  font-size: 16px;
}

.ql-snow .ql-stroke.ql-thin,
.ql-snow .ql-thin {
  stroke-width: 1px !important;
}

.quillWrapper .ql-snow.ql-toolbar {
  padding-top: 8px;
  padding-bottom: 4px;
}

.quillWrapper .ql-snow.ql-toolbar .ql-formats {
  margin-bottom: 10px;
}

.ql-snow .ql-toolbar button svg,
.quillWrapper .ql-snow.ql-toolbar button svg {
  width: 22px;
  height: 22px;
}

.quillWrapper .ql-editor ul[data-checked=false] > li::before,
.quillWrapper .ql-editor ul[data-checked=true] > li::before {
  font-size: 1.35em;
  vertical-align: baseline;
  bottom: -0.065em;
  font-weight: 900;
  color: #222;
}

.quillWrapper .ql-snow .ql-stroke {
  stroke: rgba(63, 63, 63, 0.95);
  stroke-linecap: square;
  stroke-linejoin: initial;
  stroke-width: 1.7px;
}

.quillWrapper .ql-picker-label {
  font-size: 15px;
}

.quillWrapper .ql-snow .ql-active .ql-stroke {
  stroke-width: 2.25px;
}

.quillWrapper .ql-toolbar.ql-snow .ql-formats {
  vertical-align: top;
}

.ql-picker {
  &:not(.ql-background) {
    position: relative;
    top: 2px;
  }

  &.ql-color-picker {
    svg {
      width: 22px !important;
      height: 22px !important;
    }
  }
}

.quillWrapper {
  & .imageResizeActive {
    img {
      display: block;
      cursor: pointer;
    }

    & ~ div svg {
      cursor: pointer;
    }
  }
}
</style>